<template>
    <div class="onboarding-editor">
        <div class="row q-mb-md">
            <div class="col-12 text-right">
                <q-btn
                    color="secondary"
                    class="q-mr-sm"
                    :label="$t('onboarding.existing_videos')"
                    @click="openExistingVideosModal"
                />
                <q-btn
                    color="primary"
                    icon="add"
                    :label="$t('action.add')"
                    @click="openAddModal"
                />
            </div>
        </div>

        <q-list bordered separator ref="sortableListRef">
            <q-item
                v-for="(step, index) in localSteps"
                :key="step.id"
                :data-id="step.id"
                class="q-py-md sortable-item bg-white"
            >
                <q-item-section avatar class="cursor-pointer handle">
                    <q-icon name="drag_indicator" size="md" color="grey-6" />
                </q-item-section>

                <q-item-section>
                    <q-item-label class="text-weight-bold text-subtitle1">{{
                        step.title
                    }}</q-item-label>
                    <q-item-label caption
                        >{{ $t("onboarding.step_type") }}:
                        {{ $t("onboarding.type_" + step.type) }}</q-item-label
                    >
                </q-item-section>

                <q-item-section side>
                    <div class="row items-center">
                        <q-btn
                            flat
                            round
                            icon="edit"
                            color="primary"
                            @click="openEditModal(step)"
                        />
                        <q-btn
                            flat
                            round
                            icon="delete"
                            color="negative"
                            @click="confirmDelete(step)"
                        />
                    </div>
                </q-item-section>
            </q-item>

            <q-item v-if="localSteps.length === 0">
                <q-item-section class="text-center text-grey">
                    {{ $t("onboarding.no_steps") }}
                </q-item-section>
            </q-item>
        </q-list>

        <!-- Add/Edit Dialog -->
        <q-dialog v-model="dialogVisible" persistent>
            <q-card style="min-width: 800px; max-width: 90vw">
                <q-card-section class="row items-center">
                    <div class="text-h6">
                        {{
                            isEditing
                                ? $t("onboarding.edit_step")
                                : $t("onboarding.add_step")
                        }}
                    </div>
                    <q-space />
                    <q-btn icon="close" flat round dense v-close-popup />
                </q-card-section>

                <q-card-section>
                    <q-select
                        v-model="form.type"
                        :options="typeOptions"
                        emit-value
                        map-options
                        :label="$t('onboarding.step_type')"
                        outlined
                        class="q-mb-md"
                        :disable="isEditing"
                    />

                    <q-input
                        v-model="form.title"
                        :label="$t('onboarding.step_title')"
                        outlined
                        class="q-mb-md"
                        :rules="[
                            (val) =>
                                !!val ||
                                $t('validation.required') ||
                                'Title is required',
                        ]"
                    />

                    <!-- Content editors depending on type -->
                    <div
                        v-if="
                            form.type === 'document' ||
                            form.type?.value === 'document'
                        "
                        class="q-mb-md"
                    >
                        <p class="text-subtitle2">
                            {{ $t("onboarding.content") }}
                        </p>
                        <RichTextEditor v-model="form.content.html" />
                    </div>

                    <div
                        v-if="
                            form.type === 'video' ||
                            form.type?.value === 'video'
                        "
                        class="q-mb-md"
                    >
                        <div class="q-mb-sm">
                            <q-btn-toggle
                                v-model="videoInputMode"
                                :options="[
                                    {
                                        label: $t(
                                            'onboarding.select_existing_video',
                                        ),
                                        value: 'existing',
                                    },
                                    {
                                        label: $t(
                                            'onboarding.upload_new_video',
                                        ),
                                        value: 'new',
                                    },
                                ]"
                                toggle-color="primary"
                                flat
                                clearable
                            />
                        </div>

                        <div
                            v-if="videoInputMode === 'existing'"
                            class="q-mt-md"
                        >
                            <div v-if="videos.length === 0" class="text-grey">
                                {{ $t("onboarding.no_videos") }}
                            </div>
                            <q-list v-else bordered separator>
                                <q-item
                                    v-for="video in videos"
                                    :key="video.id"
                                    clickable
                                    v-ripple
                                    @click="selectExistingVideo(video)"
                                >
                                    <q-item-section avatar>
                                        <q-img
                                            v-if="video.poster_path"
                                            :src="`/api/videos/${video.id}/poster`"
                                            ratio="16/9"
                                            style="width: 100px; height: 56px"
                                        />
                                        <q-icon
                                            v-else
                                            name="movie"
                                            size="lg"
                                            color="grey"
                                        />
                                    </q-item-section>
                                    <q-item-section>
                                        <q-item-label>{{
                                            video.title
                                        }}</q-item-label>
                                        <q-item-label
                                            caption
                                            v-if="
                                                form.content.video_id ===
                                                video.id
                                            "
                                            class="text-positive"
                                        >
                                            {{ $t("onboarding.selected") }}
                                        </q-item-label>
                                    </q-item-section>
                                </q-item>
                            </q-list>
                        </div>

                        <div v-else class="q-mt-md">
                            <VideoUploader
                                :initial-video="form.content.video"
                                @uploaded="onVideoUploaded"
                                @selected="onVideoSelected"
                                @reset="onVideoReset"
                            />
                        </div>
                    </div>

                    <div
                        v-if="
                            form.type === 'confirmation' ||
                            form.type?.value === 'confirmation'
                        "
                        class="q-mb-md"
                    >
                        <p class="text-caption">
                            {{
                                $t("onboarding.confirmation_instructions_hint")
                            }}
                        </p>
                        <q-input
                            v-model="form.content.instructions"
                            type="textarea"
                            :label="
                                $t('onboarding.instructions_for_coordinator')
                            "
                            outlined
                        />
                    </div>

                    <div
                        v-if="
                            form.type === 'email' ||
                            form.type?.value === 'email'
                        "
                        class="q-mb-md"
                    >
                        <q-select
                            v-model="form.content.recipient"
                            :options="[
                                {
                                    label:
                                        $t('onboarding.publisher') ||
                                        'Publisher',
                                    value: 'user',
                                },
                                {
                                    label:
                                        $t(
                                            'onboarding.instructions_for_coordinator',
                                        ) || 'All Coordinators',
                                    value: 'all_coordinators',
                                },
                            ]"
                            emit-value
                            map-options
                            :label="$t('onboarding.recipient')"
                            outlined
                            class="q-mb-md"
                        />
                        <q-input
                            v-model="form.content.subject"
                            :label="$t('onboarding.email_subject')"
                            outlined
                            class="q-mb-md"
                        />
                        <p class="text-subtitle2">
                            {{ $t("onboarding.email_body") }}
                        </p>
                        <RichTextEditor v-model="form.content.body" />
                    </div>
                </q-card-section>

                <q-card-actions align="right">
                    <q-btn
                        flat
                        :label="$t('button.action.cancel')"
                        color="primary"
                        v-close-popup
                    />
                    <q-btn
                        :label="$t('button.action.save')"
                        color="primary"
                        @click="saveStep"
                    />
                </q-card-actions>
            </q-card>
        </q-dialog>

        <!-- Existing Videos Management Dialog -->
        <q-dialog v-model="existingVideosDialogVisible">
            <q-card style="min-width: 600px; max-width: 90vw">
                <q-card-section class="row items-center">
                    <div class="text-h6">
                        {{ $t("onboarding.existing_videos") }}
                    </div>
                    <q-space />
                    <q-btn icon="close" flat round dense v-close-popup />
                </q-card-section>

                <q-card-section>
                    <div
                        v-if="videos.length === 0"
                        class="text-grey text-center q-pa-md"
                    >
                        {{ $t("onboarding.no_videos") }}
                    </div>
                    <q-list v-else bordered separator>
                        <q-item
                            v-for="video in videos"
                            :key="video.id"
                            class="q-py-md"
                        >
                            <q-item-section avatar>
                                <q-img
                                    v-if="video.poster_path"
                                    :src="`/api/videos/${video.id}/poster`"
                                    ratio="16/9"
                                    style="width: 120px; height: 67px"
                                />
                                <q-icon
                                    v-else
                                    name="movie"
                                    size="xl"
                                    color="grey"
                                />
                            </q-item-section>
                            <q-item-section>
                                <q-item-label
                                    class="text-subtitle1 text-weight-bold"
                                    >{{ video.title }}</q-item-label
                                >
                            </q-item-section>
                            <q-item-section side>
                                <div class="row items-center">
                                    <q-btn
                                        flat
                                        round
                                        icon="edit"
                                        color="primary"
                                        @click="editVideo(video)"
                                        :title="$t('onboarding.edit_video')"
                                    />
                                    <q-btn
                                        flat
                                        round
                                        icon="delete"
                                        color="negative"
                                        @click="confirmDeleteVideo(video)"
                                        :title="$t('action.delete')"
                                    />
                                </div>
                            </q-item-section>
                        </q-item>
                    </q-list>
                </q-card-section>
            </q-card>
        </q-dialog>

        <!-- Edit Video Dialog -->
        <q-dialog v-model="editVideoDialogVisible" persistent>
            <q-card style="min-width: 800px; max-width: 90vw">
                <q-card-section class="row items-center">
                    <div class="text-h6">{{ $t("onboarding.edit_video") }}</div>
                    <q-space />
                    <q-btn icon="close" flat round dense v-close-popup />
                </q-card-section>

                <q-card-section v-if="editingVideo">
                    <VideoUploader
                        :key="editingVideo.id"
                        :initial-video="editingVideo"
                        :show-use-video-btn="false"
                        @uploaded="onEditingVideoUpdated"
                        @selected="onEditingVideoUpdated"
                        @updated="onEditingVideoUpdated"
                        @reset="onEditingVideoReset"
                    />
                </q-card-section>

                <q-card-actions align="right">
                    <q-btn
                        flat
                        :label="$t('button.action.close')"
                        color="primary"
                        v-close-popup
                    />
                </q-card-actions>
            </q-card>
        </q-dialog>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick, getCurrentInstance } from "vue";
import { initializeSortable } from "../sortable-handler";
import RichTextEditor from "./RichTextEditor.vue";
import VideoUploader from "./VideoUploader.vue";
import { useQuasar } from "quasar";
import axios from "axios";

const props = defineProps({
    steps: {
        type: Array,
        required: true,
        default: () => [],
    },
    saveRoute: {
        type: String,
        required: true,
    },
    reorderRoute: {
        type: String,
        required: true,
    },
});

const $q = useQuasar();
const sortableListRef = ref(null);
const localSteps = ref([...props.steps]);

const dialogVisible = ref(false);
const isEditing = ref(false);
const editingId = ref(null);

const { proxy } = getCurrentInstance();
const t = proxy.$t;

const videos = ref([]);
const videoInputMode = ref("existing");

const existingVideosDialogVisible = ref(false);
const editVideoDialogVisible = ref(false);
const editingVideo = ref({ id: null, title: "" });

const typeOptions = computed(() => [
    { label: t("onboarding.type_document") || "Document", value: "document" },
    { label: t("onboarding.type_video") || "Video", value: "video" },
    {
        label: t("onboarding.type_confirmation") || "Coordinator Confirmation",
        value: "confirmation",
    },
    { label: t("onboarding.type_email") || "Trigger Email", value: "email" },
]);

const defaultForm = () => ({
    type: "document",
    title: "",
    content: {
        html: "",
        instructions: "",
        recipient: "user",
        subject: "",
        body: "",
        video_id: null,
        video: null,
    },
});

const onVideoUploaded = (video) => {
    form.value.content.video_id = video.id;
    form.value.content.video = video;
    if (!form.value.title) {
        form.value.title = video.title;
    }
};

const onVideoSelected = (video) => {
    form.value.content.video_id = video.id;
    form.value.content.video = video;
};

const onVideoReset = () => {
    form.value.content.video_id = null;
    form.value.content.video = null;
};

const form = ref(defaultForm());
let sortableInstance = null;

const fetchVideos = async () => {
    try {
        const response = await axios.get("/api/videos");
        videos.value = response.data.videos || [];
    } catch (e) {
        console.error("Error fetching videos", e);
    }
};

const selectExistingVideo = (video) => {
    form.value.content.video_id = video.id;
    form.value.content.video = video;
    if (!form.value.title) {
        form.value.title = video.title;
    }
};

const openExistingVideosModal = () => {
    fetchVideos();
    existingVideosDialogVisible.value = true;
};

const editVideo = (video) => {
    editingVideo.value = { ...video };
    editVideoDialogVisible.value = true;
};

const onEditingVideoUpdated = (updatedVideo) => {
    if (!updatedVideo) return;
    const index = videos.value.findIndex((v) => v.id === updatedVideo.id);
    if (index !== -1) {
        videos.value[index] = { ...updatedVideo };
    } else {
        videos.value.push({ ...updatedVideo });
    }

    // Also update localSteps that might reference this video
    localSteps.value.forEach((step) => {
        if (
            step.type === "video" &&
            step.content &&
            step.content.video_id === updatedVideo.id
        ) {
            step.content.video = { ...updatedVideo };
            step.content.video_id = updatedVideo.id;
        }
    });
};

const onEditingVideoReset = () => {
    fetchVideos();
};

const confirmDeleteVideo = (video) => {
    $q.dialog({
        title: t("action.delete") || "Confirm Delete",
        message: t("onboarding.delete_video_warning"),
        cancel: true,
        persistent: true,
    }).onOk(async () => {
        try {
            await axios.delete(`/api/videos/${video.id}`);
            videos.value = videos.value.filter((v) => v.id !== video.id);
            // Remove from steps where it was used
            localSteps.value = localSteps.value.filter(
                (s) =>
                    !(
                        s.type === "video" &&
                        s.content &&
                        s.content.video_id === video.id
                    ),
            );
            $q.notify({ type: "positive", message: "Video deleted" });
        } catch (e) {
            $q.notify({ type: "negative", message: "Error deleting video" });
        }
    });
};

onMounted(() => {
    fetchVideos();
    nextTick(() => {
        // Find the actual DOM element of the QList
        const el = sortableListRef.value?.$el;
        if (el) {
            // Need to pass element ID to the handler as it expects an ID.
            // Let's modify the handler or give the element an ID.
            el.id = "onboarding-steps-list";
            sortableInstance = initializeSortable("onboarding-steps-list", {
                animation: 150,
                handle: ".handle",
                onEnd: handleDragEnd,
            });
        }
    });
});

const handleDragEnd = async (evt) => {
    if (evt.oldIndex === evt.newIndex) return;

    // Update local array order
    const movedItem = localSteps.value.splice(evt.oldIndex, 1)[0];
    localSteps.value.splice(evt.newIndex, 0, movedItem);

    // Get new order of IDs
    const stepIds = localSteps.value.map((step) => step.id);

    try {
        await axios.post(props.reorderRoute, { steps: stepIds });
        $q.notify({ type: "positive", message: "Order saved" });
    } catch (e) {
        $q.notify({ type: "negative", message: "Error saving order" });
        // Optionally reload or revert order here
    }
};

const openAddModal = () => {
    isEditing.value = false;
    editingId.value = null;
    form.value = defaultForm();
    dialogVisible.value = true;
};

const openEditModal = (step) => {
    isEditing.value = true;
    editingId.value = step.id;

    const stepContent =
        step.content && typeof step.content === "object" ? step.content : {};

    // Clone step values
    form.value = {
        type: step.type,
        title: step.title,
        content: {
            ...defaultForm().content,
            ...JSON.parse(JSON.stringify(stepContent)),
        },
    };

    dialogVisible.value = true;
};

const saveStep = async () => {
    if (!form.value.title) {
        $q.notify({ type: "warning", message: "Please enter a title" });
        return;
    }

    try {
        // Use either the selected object {label, value} or string
        const typeValue =
            typeof form.value.type === "object"
                ? form.value.type.value
                : form.value.type;

        const payload = {
            type: typeValue,
            title: form.value.title,
            content: form.value.content,
        };

        if (isEditing.value) {
            const res = await axios.put(
                `${props.saveRoute}/${editingId.value}`,
                payload,
            );
            const index = localSteps.value.findIndex(
                (s) => s.id === editingId.value,
            );
            if (index !== -1) {
                localSteps.value[index] = res.data.step;
            }
            $q.notify({ type: "positive", message: "Step updated" });
        } else {
            const res = await axios.post(props.saveRoute, payload);
            localSteps.value.push(res.data.step);
            $q.notify({ type: "positive", message: "Step added" });
        }

        dialogVisible.value = false;
    } catch (e) {
        $q.notify({ type: "negative", message: "Error saving step" });
    }
};

const confirmDelete = (step) => {
    $q.dialog({
        title: "Confirm",
        message: `Delete step "${step.title}"?`,
        cancel: true,
        persistent: true,
    }).onOk(async () => {
        try {
            await axios.delete(`${props.saveRoute}/${step.id}`);
            localSteps.value = localSteps.value.filter((s) => s.id !== step.id);
            $q.notify({ type: "positive", message: "Step deleted" });
        } catch (e) {
            $q.notify({ type: "negative", message: "Error deleting step" });
        }
    });
};
</script>

<style scoped>
.sortable-item {
    transition: background-color 0.2s;
}
.sortable-item:hover {
    background-color: #f5f5f5 !important;
}
</style>
